import * as React from "react";

import { WikiType } from "TFS/Wiki/Contracts";
import { RepoConstants, WikiActionIds } from "Wiki/Scripts/CommonConstants";
import { PerformanceConstants } from "Wiki/Scripts/CustomerIntelligenceConstants";
import { getPageNameFromPath } from "Wiki/Scripts/Helpers";
import * as WikiResources from "Wiki/Scripts/Resources/TFS.Resources.Wiki";
import * as WikiFeatures from "Wiki/Scripts/WikiFeatures";
import { ShortcutCode, ShortcutListener, WikiHubShortcutGroup } from "Wiki/Scripts/WikiKeyboardShortcuts";

import * as CloneWikiDialog from "Wiki/Scenarios/Overview/Components/CloneWikiDialog";
import * as RenameWikiDialog from "Wiki/Scenarios/Overview/Components/RenameWikiDialog";
import { PageContent } from "Wiki/Scenarios/Overview/Components/PageContent";
import { TemplatePickerDialogParams } from "Wiki/Scenarios/Overview/ViewActionsHub";
import { ViewStoresHub } from "Wiki/Scenarios/Overview/Stores/ViewStoresHub";
import { WikiPagesState } from "Wiki/Scenarios/Overview/Stores/WikiPagesStore";
import { ViewActionCreator } from "Wiki/Scenarios/Overview/ViewActionCreator";
import { WikiBreadCrumb } from "Wiki/Scenarios/Shared/Components/WikiBreadCrumb";

import "VSS/LoaderPlugins/Css!Wiki/Scenarios/Overview/Components/OverviewContainer";

export interface ContainerProps {
    actionCreator: ViewActionCreator;
    storesHub: ViewStoresHub;
    onContentRendered?(scenario?: string, data?: any): void;
}

export interface OverviewContainerProps extends ContainerProps {
    onFilterShortcut(): void;
}

export interface OverviewState {
    isCloneWikiDialogOpen: boolean;
    isRenameWikiDialogOpen: boolean;
}

export class OverviewContainer extends React.Component<OverviewContainerProps, OverviewState> implements ShortcutListener {
    private _isFirstContentRendered = false;
    private _renderedPagePath: string = null;
    private _wikiShortcuts: WikiHubShortcutGroup;
    private _pageContentRef: PageContent;

    constructor(props: OverviewContainerProps) {
        super(props);

        this._wikiShortcuts = new WikiHubShortcutGroup();
        this.state = {
            isCloneWikiDialogOpen: false,
            isRenameWikiDialogOpen: false,
        };
    }

    public componentDidMount(): void {
        this.props.storesHub.pageStore.addChangedListener(this._onPageChanged);
        this.props.storesHub.cloneWikiStore.addChangedListener(this._onCloneWikiStateChanged);
        this.props.storesHub.renameWikiStore.addChangedListener(this._onRenameWikiStateChanged);

        this._wikiShortcuts.registerWikiShortcuts(this.onShortcutPressed);
        this._onOverviewRenderComplete();
    }

    public componentWillUnmount(): void {
        if (!this.props.storesHub) {
            return;
        }
        this._wikiShortcuts.dispose();

        if (this.props.storesHub.pageStore) {
            this.props.storesHub.pageStore.removeChangedListener(this._onPageChanged);
        }

        if (this.props.storesHub.cloneWikiStore) {
            this.props.storesHub.cloneWikiStore.removeChangedListener(this._onCloneWikiStateChanged);
        }

        if (this.props.storesHub.renameWikiStore) {
            this.props.storesHub.renameWikiStore.removeChangedListener(this._onRenameWikiStateChanged);
        }
    }

    public render(): JSX.Element {
        const state = this.props.storesHub.state;

        let pagePath = state.sharedState.urlState.pagePath;
        const commonState = this.props.storesHub.getAggregateState().sharedState.commonState;

        const cloneWikiDialogProps: CloneWikiDialog.CloneWikiDialogProps = {
            cloneUrl: commonState.cloneUrl,
            headingLevel: 1,
            isOpen: this.state.isCloneWikiDialogOpen,
            onDismiss: this.props.actionCreator.hideWikiCloneDialog,
            repositoryContext: commonState.repositoryContext,
            sshEnabled: commonState.sshEnabled,
            sshUrl: commonState.sshUrl,
            publishCopyCloneUrlClicked: this.props.actionCreator.publishCopyCloneUrlClicked
        };

        const renameWikiDialogProps: RenameWikiDialog.RenameWikiDialogProps = {
            onDismiss: this.props.actionCreator.hideWikiRenameDialog,
            isOpen: this.state.isRenameWikiDialogOpen,
            name: this.props.storesHub.getAggregateState().sharedState.commonState.wiki.name,
            onSave: this.props.actionCreator.saveWikiName,
            errorMessage: this.props.storesHub.state.renameWikiState.errorMessage,
            isRenameInProgress: this.props.storesHub.state.renameWikiState.isRenameInProgress,
        };

        const isImmersiveWikiEnabled: boolean = WikiFeatures.isImmersiveWikiEnabled();

        return (
            <div className="overview-container">
                {isImmersiveWikiEnabled ||
                    <WikiBreadCrumb
                        currentAction={WikiActionIds.View}
                        currentWiki={state.sharedState.commonState.wiki}
                        currentWikiVersion={state.sharedState.commonState.wikiVersion}
                        onCreateWiki={this._onCreateWiki}
                        onPublishWikiAction={this._onPublishWiki}
                        onUnpublishWikiAction={this._onUnpublishWiki}
                        hideProjectWikiAction={!this._showCreateProjectWikiAction()}
                        hidePublishWikiAction={!this._showPublishWikiAction()}
                        hideUnpublishWikiAction={!this._showUnpublishWikiAction()}
                    />
                }
                <PageContent
                    editPage={this._editPage}
                    newPage={this._newPage}
                    containerProps={this.props}
                    rawMarkdownContent={state.pageState.content}
                    isPageContentLoading={state.pageState.isLoading}
                    cloneWikiDialogProps={cloneWikiDialogProps}
                    renameWikiDialogProps={renameWikiDialogProps}
                    onCloneWikiClick={this.props.actionCreator.promptWikiCloneDialog}
                    onRenameWikiClick={this.props.actionCreator.promptWikiRenameDialog}
                    onWikiSecurityClick={this.props.actionCreator.showWikiSecurityDialog}
                    title={getPageNameFromPath(pagePath)}
                    ref={this._savePageContentRef}
                    onEditInDraftVersionClick={this.props.actionCreator.promptEditInDraftVersionDialog}
                />
            </div>
        );
    }

    public componentDidUpdate(): void {
        this._onOverviewRenderComplete();

        const pagePath = this.props.storesHub.state.pageState.path;

        // RenderPage if pagePath in url is not the one already rendered and if the pages have loaded
        if (pagePath && pagePath !== this._renderedPagePath) {
            this._renderIfPagesHaveLoaded(pagePath);
        }
    }

    public onShortcutPressed = (shortcut: ShortcutCode) => {
        switch (shortcut) {
            case ShortcutCode.NewPage:
                return this._newPage();
            case ShortcutCode.EditPage:
                return this._editPage();
            case ShortcutCode.CreateSubPage:
                return this._onCreateSubpageShortcut();
            case ShortcutCode.FilterPages:
                return this._onFilterShortcut();
            case ShortcutCode.PrintPage:
                return this._printPage();
        }
    }

    private _showCreateProjectWikiAction = (): boolean => {
        const state = this.props.storesHub.state;

        /*
            Show 'Create project wiki' action if,
            1. Project wiki is not already existing, and
            2. User has create repository permission
        */
        return !state.sharedState.commonState.isProjectWikiExisting
            && state.sharedState.permissionState.hasCreatePermission;
    }

    private _showPublishWikiAction = (): boolean => {
        const state = this.props.storesHub.state;

        /*
            Show 'Create code wiki' action if,
            1. Project is not tfvc only
            2. Code hub service is turned on
            3. User has repo Contribute permission
        */
        return !state.sharedState.commonState.isTfvcOnlyProject 
            &&  WikiFeatures.isCodeHubEnabled() 
            && state.sharedState.permissionState.hasContributePermission
    }

    private _showUnpublishWikiAction(): boolean {
        const state = this.props.storesHub.state.sharedState;

        /*
         * Show 'Unpublish wiki' action if,
         * 1. Current wiki is a code wiki, and
         * 2. User has repo Contribute permission
         */
        return WikiFeatures.isUnpublishWikiEnabled()
            && state.commonState.wiki.type === WikiType.CodeWiki
            && state.permissionState.hasContributePermission;
    }

    private _onCreateWiki = () => {
        this.props.actionCreator.createProjectWiki();
    }

    private _onPublishWiki = () => {
        this.props.actionCreator.publishWiki();
    }

    private _onUnpublishWiki = () => {
        this.props.actionCreator.promptUnpublishWikiDialog();
    }

    private _onCreateSubpageShortcut = () => {
        let pagePath = this.props.storesHub.state.sharedState.urlState.pagePath;
        this.props.actionCreator.addPage(pagePath);
    }

    private _onFilterShortcut = () => {
        this.props.onFilterShortcut();
    }

    private _savePageContentRef = (ref: PageContent) => {
        this._pageContentRef = ref;
    }

    private _printPage(): void {
        this._pageContentRef && this._pageContentRef.populateFrameAndPrint();
        this.props.actionCreator.publishPagePrinted();
    }

    // WARNINIG: DEPRECATED!!! Do not use this method any more. 
    // Not removing this for now as im unaware of impact but with recent changes this method should just be a no-op
    // Task 1419098: remove dependency on methods '_renderIfPagesHaveLoaded' for both edit pagecontainer and overviewpagecontainer
    private _renderIfPagesHaveLoaded(pagePath: string): void {
        // Call renderPage only if the wiki pages are loaded
        const wikiPagesState: WikiPagesState = this.props.storesHub.state.wikiPagesState;
        const sharedState = this.props.storesHub.state.sharedState;

        if (wikiPagesState && wikiPagesState.wikiPages && Object.keys(wikiPagesState.wikiPages).length > 0) {
            this._renderedPagePath = pagePath;

            /* Call render only if action is view, edit action handled by EditPageContainer */
            if (sharedState.urlState.action === WikiActionIds.View) {
                this.props.actionCreator.renderPage(pagePath, true, false);
            }
        }
    }

    private _editPage = (clickSource?: string) => {
        const pagePath = this.props.storesHub.state.pageState.path;
        this.props.actionCreator.editPage(pagePath, undefined, undefined, undefined, clickSource);
    }

    private _newPage = (clickEvent?: boolean) => {
        if (WikiFeatures.isImmersiveWikiEnabled()) {
            this.props.actionCreator.promptTemplatePickerDialog(undefined, clickEvent);
        } else {
            this.props.actionCreator.addPage(undefined, undefined, undefined, undefined, undefined, clickEvent);
        }
    }

    private _onPageChanged = (): void => {
        this.forceUpdate();
    }

    private _onCloneWikiStateChanged = (): void => {
        this.setState({ isCloneWikiDialogOpen: this.props.storesHub.cloneWikiStore.state.isCloneWikiDialogOpen } as OverviewState);
    }

    private _onRenameWikiStateChanged = (): void => {
        this.setState({ isRenameWikiDialogOpen: this.props.storesHub.renameWikiStore.state.isRenameWikiDialogOpen } as OverviewState);
    }

    private _onOverviewRenderComplete(): void {
        const pageState = this.props.storesHub.state.pageState;
        const isContentAvailable = pageState.path
            && !pageState.isLoading
            && (pageState.content || pageState.content === "");

        if (isContentAvailable) {
            if (this.props.onContentRendered) {
                let data: { [key: string]: any } = {};
                data[PerformanceConstants.IsFirstRender] = !this._isFirstContentRendered;
                this.props.onContentRendered(PerformanceConstants.Overview, data);
            }

            this._isFirstContentRendered = true;
        }
    }
}
